/**
 * Testi italiani della scheda tecnica del progetto (ADR 0048): la panoramica a sezioni,
 * lo stato delle modifiche richieste e il cassetto con cui l'utente le chiede.
 *
 * Due avvisi vanno letti con attenzione, perché il testo **è** il comportamento:
 *
 *  - `drawer.credentials.notice`: le credenziali di uno storage o di un database
 *    dell'utente si salvano nella piattaforma e **non arrivano ancora al sito**. La frase
 *    lo dice invece di lasciarlo scoprire;
 *  - `drawer.consent`: la richiesta diventa un messaggio all'Institore, che lavora sul
 *    codice. Non è un interruttore che cambia qualcosa da solo.
 */

/** Testi della scheda tecnica. */
export const projectProfile = {
  /** Intestazione della pagina del progetto. */
  header: {
    /** Link al sito pubblicato. */
    site: 'Apri il sito',
    /** Nome accessibile del link al sito. */
    siteAccessible: 'Apri il sito {{url}} (si apre in una nuova scheda)',
    /** Link al set che ospita il progetto. */
    hostSet: 'Set {{name}}',
  },

  /** Riga di riepilogo in testa alla panoramica. */
  summary: {
    /** Titolo della riga. */
    title: 'Scheda tecnica',
    /** Quante sezioni hanno dichiarato gli agenti. */
    declared: '{{count}} sezioni su {{total}} compilate dagli agenti.',
    /** Nessuna sezione dichiarata. */
    declaredNone: 'Gli agenti non hanno ancora compilato la scheda.',
    /** Ultimo aggiornamento. */
    updated: 'Ultimo aggiornamento il {{date}}, da {{who}}.',
    /** Comando che chiede all'Institore di compilare o aggiornare la scheda. */
    refresh: 'Chiedi un aggiornamento',
    /** Nessun set ospita il progetto. */
    noHost: "Nessun set ospita questo progetto. Le modifiche si chiedono all'Institore di un set che lo ha in scrittura.",
    /** Il set lo ha in sola lettura. */
    readOnlyHost: "Il set {{name}} ha il progetto in sola lettura: per chiedere modifiche serve un set che lo abbia in scrittura.",
    /** Lettura della scheda non riuscita. */
    loadFailed: 'Caricamento della scheda non riuscito. Riprova.',
  },

  /** Da dove viene un dato. */
  source: {
    /** Scritto dagli agenti. */
    declared: 'Dagli agenti',
    /** Saputo dalla piattaforma. */
    platform: 'Dalla piattaforma',
    /** Valore predefinito, non verificato. */
    default: 'Predefinito',
  },

  /** Comandi comuni alle sezioni. */
  actions: {
    /** Apre il cassetto di una modifica. */
    ask: 'Chiedi una modifica',
    /** Nome accessibile di «Chiedi una modifica», con la sezione. */
    askAccessible: 'Chiedi una modifica: {{section}}',
    /** Cambia una scelta (storage, database). */
    change: 'Cambia',
    /** Nome accessibile di «Cambia», con la scelta. */
    changeAccessible: 'Cambia {{section}}',
    /** Chiede all'Institore di compilare la scheda. */
    fill: 'Chiedi la compilazione',
    /** Nome accessibile di «Chiedi la compilazione», con la sezione. */
    fillAccessible: 'Chiedi la compilazione: {{section}}',
  },

  /** Valore non dichiarato. */
  notDeclared: 'Non ancora dichiarato.',
  /** Chi ha scritto una sezione e quando. */
  declaredBy: 'Dichiarato da {{who}} il {{date}}',
  /** Sì e no. */
  yes: 'Sì',
  no: 'No',

  /** Durate, nell'unità più grande che le divide esattamente. */
  duration: {
    days_one: '{{count}} giorno',
    days_other: '{{count}} giorni',
    hours_one: '{{count}} ora',
    hours_other: '{{count}} ore',
    minutes_one: '{{count}} minuto',
    minutes_other: '{{count}} minuti',
    seconds_one: '{{count}} secondo',
    seconds_other: '{{count}} secondi',
  },

  /** Cambio dell'indirizzo del sito di un progetto esistente (ADR 0054 §10). */
  subdomainChange: {
    /** Pulsante nella sezione «Sito e hosting». */
    open: 'Cambia indirizzo',
    /** Titolo della finestra. */
    title: 'Cambia l’indirizzo del sito',
    /** Spiegazione sopra il campo. */
    intro: 'La produzione prende questo nome e la demo lo stesso nome con -demo alla fine.',
    /** Avviso sopra la conferma. */
    warning:
      'Gli indirizzi di oggi rimandano ai nuovi. I link della demo già dati smettono di valere: ne serve uno nuovo dalla pagina Hosting del set.',
    /** Pulsante di conferma. */
    confirm: 'Cambia indirizzo',
    /** Pulsante che chiude l’esito. */
    close: 'Chiudi',
    /** Errore generico. */
    failed: 'Cambio non riuscito. Riprova.',
    /** Esito con siti rinominati. */
    saved: 'Indirizzo cambiato. I siti ora rispondono qui:',
    /** Esito senza siti pubblicati. */
    savedNoSites: 'Indirizzo salvato. Demo e produzione lo useranno quando nascono.',
    /** Nome dell’ambiente. */
    env: {
      demo: 'Demo privata',
      prod: 'Produzione',
    },
    /** Indirizzo di prima. */
    was: 'prima {{host}}',
    /** Titolo dei passi per Google. */
    googleTitle: 'Accesso con Google del sito',
    /** Passi per Google. */
    googleText:
      'Se il sito usa un client OAuth di Google suo, apri Google Cloud Console, API e servizi, Credenziali, e nel client del sito aggiungi questi indirizzi alle «Origini JavaScript autorizzate». Se il sito usa anche gli «URI di reindirizzamento autorizzati», aggiungi lì gli stessi indirizzi con il percorso di ritorno del sito.',
    /** Che cosa succede dopo. */
    after:
      'L’Institore riceve un messaggio con gli indirizzi nuovi: aggiorna il codice del sito e ripubblica la demo. La produzione la rilascia solo se glielo chiedi.',
  },

  /** Sezione «Sito e hosting». */
  site: {
    title: 'Sito e hosting',
    url: 'Indirizzo',
    demoUrl: 'Demo privata',
    demoHelp: 'Si apre con un link della demo dalla pagina Hosting del set.',
    prodUrl: 'Produzione',
    state: 'Stato',
    sleep: 'Sito',
    certificate: 'Certificato',
    space: 'Spazio dei volumi',
    spaceValue: '{{allocated}} GiB usati su {{chosen}} GiB',
    spaceChosen: '{{chosen}} GiB scelti',
    set: 'Set che lo ospita',
    mode: 'Modalità',
    noHost: 'Nessun set ospita il progetto.',
    notPublished: 'Il progetto non è pubblicato.',
    openSet: 'Apri il set',
    external: 'Spazio esistente, collegato da un amministratore',
    loadFailed: "Caricamento dell'hosting non riuscito. Riprova.",
  },

  /**
   * Sezione «Analisi SEO» (ADR 0059): il pulsante manda all'Institore un messaggio normale,
   * e lo specialista della piattaforma fa l'analisi. `message` è il testo di quel messaggio.
   */
  seo: {
    title: 'Analisi SEO',
    intro: "Lo specialista SEO della piattaforma legge il sito pubblicato e propone le correzioni. Il risultato arriva nella chat dell'Institore.",
    environment: 'Sito da analizzare',
    ask: 'Controllo SEO',
    message: 'Controlla la SEO del sito di {{environment}} del progetto «{{name}}» con lo specialista SEO della piattaforma.',
    environmentWord: {
      prod: 'produzione',
      demo: 'demo',
    },
    sent: "Richiesta inviata all'Institore.",
    sentDetail: 'Il risultato arriva nella chat del set {{set}} fra qualche minuto.',
    openChat: 'Apri la chat',
    noSite: 'Il progetto non ha un sito pubblicato.',
    readOnly: 'Per chiedere un controllo serve un set con il progetto in scrittura.',
    plans: 'Con i piani Base e Premium, al più 3 analisi al giorno per set.',
    recent: 'Analisi recenti',
    empty: 'Nessuna analisi.',
    loadFailed: 'Caricamento delle analisi non riuscito. Riprova.',
    followUp: 'Domanda successiva',
    page: 'Pagina {{page}}',
    score: 'Punteggio {{score}} su 100',
    counts: '{{critical}} critici, {{high}} alti',
    report: 'Scarica il rapporto',
    reportAccessible: 'Scarica il rapporto del {{date}}',
    reportFailed: 'Scaricamento non riuscito. Riprova.',
    reportSaved: 'Rapporto scaricato.',
    status: {
      queued: 'In coda',
      starting: 'In avvio',
      running: 'In corso',
      completed: 'Completata',
      failed: 'Non riuscita',
      cancelled: 'Annullata',
      expired: 'Scaduta',
    },
    env: {
      prod: 'Produzione',
      demo: 'Demo',
    },
    errors: {
      queue_full: "La coda dei messaggi del set è piena. Riprova quando l'Institore ha finito.",
      token_limit_reached: 'Hai usato i token del periodo inclusi nel piano. Accendi il consumo a fine mese o aspetta il periodo nuovo.',
      failed: 'Invio non riuscito. Riprova.',
    },
  },

  /** Sezione «Accesso degli utenti». */
  auth: {
    title: 'Accesso degli utenti',
    methods: 'Metodi',
    none: "L'app non ha accesso degli utenti.",
    session: 'Sessione',
    renewal: 'Rinnovo',
    password: 'Password',
    reset: 'Recupero della password',
    lockout: 'Blocco dei tentativi',
    emailVerification: "Conferma dell'email",
    twoFactor: 'Verifica in due passaggi',
    registration: 'Registrazione',
    notes: 'Note',
    method: {
      email_password: 'Email e password',
      magic_link: 'Link via email',
      oauth: 'Accesso con {{provider}}',
      oauthGeneric: 'Accesso con un account esterno',
      email_code: 'Codice via email',
      sms_code: 'Codice via SMS',
      totp: 'App di autenticazione',
      passkey: 'Passkey',
      api_key: 'Chiave API',
      other: 'Altro metodo',
    },
    mechanism: {
      cookie_jwt: 'Cookie con token firmato',
      cookie_session: 'Cookie di sessione sul server',
      bearer_token: "Token nell'intestazione delle richieste",
      other: 'Altro meccanismo',
    },
    sessionValue: '{{mechanism}}, dura {{duration}}',
    sessionNoLimit: '{{mechanism}}, durata non dichiarata',
    idle: 'Uscita dopo {{duration}} senza attività',
    renewalValue: {
      none: 'Nessuno: alla scadenza si accede di nuovo',
      sliding: 'Si allunga a ogni uso',
      refresh_token: 'Con un token di aggiornamento',
      other: 'Altro',
    },
    passwordRange: 'Da {{min}} a {{max}} caratteri',
    passwordMin: 'Almeno {{min}} caratteri',
    characters: {
      lower: 'minuscole',
      upper: 'maiuscole',
      digit: 'cifre',
      symbol: 'simboli',
    },
    charactersValue: 'Obbligatori: {{list}}',
    hashValue: 'Salvate con {{hash}}',
    expiry: 'Scadono dopo {{days}} giorni',
    resetValue: {
      none: 'Non previsto',
      email_link: 'Link via email',
      email_code: 'Codice via email',
      admin: 'Da un amministratore',
      other: 'Altro',
    },
    lockoutValue: '{{attempts}} tentativi falliti in {{window}}',
    lockoutScope: {
      ip_and_account: 'per indirizzo e account',
      account: 'per account',
      ip: 'per indirizzo',
      other: 'altro criterio',
    },
    lockoutVolatile: 'Il conteggio si azzera al riavvio del server.',
    lockoutNone: 'Nessun blocco',
    twoFactorValue: {
      none: 'Non disponibile',
      optional: 'Facoltativa',
      required: 'Obbligatoria',
    },
    registrationValue: {
      open: 'Aperta a tutti',
      invite_only: 'Solo su invito',
      closed: 'Chiusa',
      other: 'Altro',
    },
  },

  /** Sezione «Dati». */
  data: {
    title: 'Dati',
    database: 'Database',
    storage: 'File degli utenti',
    engine: {
      postgresql: 'PostgreSQL',
      mysql: 'MySQL',
      mariadb: 'MariaDB',
      sqlite: 'SQLite',
      mongodb: 'MongoDB',
      redis: 'Redis',
      valkey: 'Valkey',
      other: 'Altro database',
    },
    location: {
      site_namespace: 'sulla piattaforma, nello spazio del sito',
      external: 'fuori dalla piattaforma',
      embedded: "dentro l'app",
      other: 'altrove',
    },
    databaseDefault: 'Non dichiarato. Predefinito: PostgreSQL sulla piattaforma.',
    databaseNone: "L'app non usa database.",
    storageKind: {
      none: "Nessuno: l'app non salva file",
      platform_r2: "Storage della piattaforma (R2, dati nell'UE)",
      external_s3: 'Storage S3 tuo',
      volume: 'Volume nello spazio del sito',
      other: 'Altro storage',
    },
    storageDefault: 'Non dichiarato. Predefinito: nessuno storage.',
    usage: 'Che cosa contiene',
    uploads: 'Caricamenti',
    directUploads: 'I file vanno dal browser al bucket senza passare dal server.',
    bucket: 'Bucket del progetto',
    bucketValue: '{{name}}: {{count}} file, {{size}}',
    bucketMeasured: 'Misurato il {{date}}.',
    bucketNotMeasured: '{{name}}: uso non ancora misurato',
    bucketNone: 'La piattaforma non ha ancora creato il bucket.',
    storageOff: 'Lo storage della piattaforma non è attivo.',
  },

  /** Sezione «Codice e versioni». */
  code: {
    title: 'Codice e versioni',
    repository: 'Repository',
    platformRepo: 'GitHub di institor',
    userRepo: 'GitHub tuo',
    otherRepo: 'Altro repository',
    repositoryLink: 'Apri {{repository}} su GitHub (si apre in una nuova scheda)',
    managedState: {
      ready: 'Pronto',
      failed: 'Creazione non riuscita',
      transferring: 'In trasferimento',
      migrated: 'Spostato sul tuo account',
    },
    none: "Il repository non c'è ancora: il codice per ora è solo nel volume del set.",
    defaultNote:
      'Ogni progetto ha un repository. Se non ne colleghi uno tuo, la piattaforma lo crea sul GitHub di institor alla prossima partenza del set.',
    autosave: 'Salvataggio automatico',
    autosaveState: {
      on: 'Attivo: il lavoro del set va sul ramo institor/autosave a ogni task chiuso e almeno ogni ora',
      pending: "Parte dopo l'approvazione dell'analisi",
      off: 'Spento: il set non scrive su questo repository',
    },
    move: 'Sposta sul tuo GitHub',
    cloneUrl: 'Indirizzo per i cloni',
    mode: 'Modalità',
    source: 'Contenuto iniziale',
    sources: {
      empty: 'Progetto vuoto',
      git: 'Da un repository',
      upload: 'Da una cartella caricata',
      link: 'Progetto collegato',
    },
    commit: 'Codice descritto dalla scheda',
  },

  /** Sezione «Tecnologie». */
  stack: {
    title: 'Tecnologie',
    languages: 'Linguaggi',
    frontend: 'Interfaccia web',
    backend: 'Server',
    mobile: 'App mobile',
    runtime: 'Dove gira',
    libraries: 'Librerie principali',
    tests: 'Test',
    integrations: 'Servizi esterni',
    integrationKind: {
      email: 'Email',
      payments: 'Pagamenti',
      analytics: 'Statistiche',
      maps: 'Mappe',
      ai: 'Intelligenza artificiale',
      notifications: 'Notifiche',
      identity: 'Identità',
      other: 'Altro',
    },
    integrationValue: '{{provider}} ({{kind}})',
  },

  /** Sezione «App mobile». */
  mobile: {
    title: 'App mobile',
    platforms: 'Piattaforme',
    platform: {
      android: 'Android',
      ios: 'iOS',
    },
    framework: 'Fatta con',
    frameworks: {
      expo: 'Expo',
      react_native: 'React Native',
      flutter: 'Flutter',
      native: 'Codice nativo',
      other: 'Altro',
    },
    androidPackage: 'Pacchetto Android (Google Play)',
    iosBundle: 'Bundle iOS (App Store)',
    credentials: 'Credenziali degli store',
    credentialsValue: '{{count}} su 4 salvate',
    lastRelease: 'Ultima pubblicazione',
    lastReleaseValue: '{{version}} su {{store}}: {{state}}',
    none: 'Nessuna app mobile.',
    manage: 'Gestisci la pubblicazione',
  },

  /** Sezione «Origine». */
  origin: {
    title: 'Origine',
    template: 'Template di partenza',
    noTemplate: 'Nessun template dichiarato.',
    templatesLink: 'Apri i template',
    createdBy: 'Creato da',
    createdAt: 'Creato il',
    slug: 'Slug',
    archivedAt: 'Archiviato il',
    description: 'Descrizione',
    unknownPerson: 'Utente non noto',
  },

  /** Sezione «In breve»: il progetto in parole semplici, letto anche dalla voce. */
  inBrief: {
    title: 'In breve',
    none: 'Gli agenti non hanno ancora scritto la descrizione in parole semplici.',
    structure: 'Com’è fatto',
    limits: 'Limiti noti',
    faq: 'Domande frequenti',
  },

  /** Stato delle modifiche richieste. */
  requests: {
    state: {
      requested: 'Da consegnare',
      delivered: 'Consegnata',
      in_progress: 'In corso',
      applied: 'Applicata',
      rejected: 'Rifiutata',
      cancelled: 'Annullata',
    },
    topic: {
      storage: 'File degli utenti',
      database: 'Database',
      auth: 'Accesso degli utenti',
      profile: 'Scheda tecnica',
      release: 'Rilascio in produzione',
      requirements: 'Requisiti funzionali',
    },
    line: '{{topic}}: {{target}}. Chiesta da {{who}} il {{date}}.',
    outcome: "Nota dell'Institore: {{text}}",
    cancel: 'Annulla richiesta',
    cancelAccessible: 'Annulla la richiesta: {{topic}}',
    cancelQuestion: 'Annullare la richiesta?',
    cancelConsequence: "L'Institore la trova chiusa e non la porta avanti. Il lavoro già fatto resta.",
    cancelled: 'Richiesta annullata.',
    cancelFailed: 'Annullamento non riuscito. Riprova.',
    sent: "Richiesta consegnata all'Institore del set {{set}}.",
    openConversation: 'Apri la conversazione',
    someone: 'un utente',
  },

  /** Cassetto della modifica. */
  drawer: {
    title: {
      storage: 'File degli utenti',
      database: 'Database',
      auth: 'Accesso degli utenti',
      profile: 'Aggiorna la scheda',
      release: 'Manda in produzione',
      requirements: 'Allinea ai requisiti',
    },
    submit: "Invia all'Institore",
    consent:
      "La richiesta arriva all'Institore nella conversazione del set. Lui cambia il codice, chiede conferma prima di spostare dati e aggiorna lo stato qui.",
    profileIntro: "L'Institore legge il codice e compila o corregge tutte le sezioni della scheda. Il codice non cambia.",
    choice: 'Scelta',
    current: 'Ora: {{value}}',
    target: {
      storage: {
        none: 'Nessuno storage',
        platform: 'Storage della piattaforma (R2)',
        external: 'Storage S3 tuo',
      },
      database: {
        cluster: 'PostgreSQL sulla piattaforma',
        external: 'Database tuo, fuori dalla piattaforma',
      },
      auth: {
        custom: 'Modifiche all’accesso',
        institor: 'Accesso con Google di institorAI',
      },
      profile: {
        refresh: 'Compila o aggiorna la scheda',
      },
      release: {
        production: 'Porta in produzione la versione della demo',
      },
      requirements: {
        sync: 'Allinea il codice ai requisiti funzionali',
      },
    },
    targetHelp: {
      none: "L'app smette di salvare file. Quelli già salvati restano dove sono finché l'Institore non ti chiede che cosa farne.",
      platform: "I file vanno nel bucket del progetto, con i dati nell'UE. Lo spazio conta nello spazio del progetto.",
      platformOff: 'Lo storage della piattaforma non è attivo: chiedi a un amministratore.',
      external: 'I file vanno in un bucket compatibile S3 di un fornitore che scegli tu.',
      cluster: 'Un database PostgreSQL in un pod dello spazio del sito, con un volume dentro lo spazio scelto.',
      externalDb: 'Il database lo gestisci tu o un fornitore (per esempio Neon o Supabase).',
      authCustom: "Scegli che cosa cambiare nei metodi e nelle regole dell'accesso.",
      authInstitor:
        'Gli utenti entrano con Google tramite institorAI e Google mostra il nome institorAI. Il sito non ha bisogno di configurare Google. Demo e produzione hanno account separati.',
    },
    storage: {
      endpoint: 'Endpoint S3',
      endpointHelp: 'Solo https e il nome del server, per esempio https://s3.eu-central-1.amazonaws.com.',
      bucket: 'Bucket',
      region: 'Regione',
      regionHelp: 'Lascia vuoto se il fornitore non la chiede.',
      provider: 'Fornitore',
      accessKey: 'Chiave di accesso',
      secretKey: 'Chiave segreta',
    },
    database: {
      engine: 'Motore',
      host: 'Host',
      hostHelp: 'Solo il nome del server con la porta, per esempio db.esempio.it:5432.',
      name: 'Nome del database',
      provider: 'Fornitore',
      connection: 'Stringa di connessione',
      connectionHelp: 'Con utente e password. Si salva fra le credenziali del progetto e non si rilegge.',
    },
    auth: {
      addMethods: 'Metodi da aggiungere',
      removeMethods: 'Metodi da togliere',
      methodsPlaceholder: 'Scegli i metodi',
      googleNote: "L'accesso con Google usa lo strumento Google del progetto.",
      sessionHours: 'Durata della sessione (ore)',
      passwordMinLength: 'Lunghezza minima delle password',
      lockoutAttempts: 'Tentativi falliti prima del blocco',
      twoFactor: 'Verifica in due passaggi',
      twoFactorKeep: 'Non cambiare',
      emailVerification: "Conferma dell'email",
      emailKeep: 'Non cambiare',
      emailOn: 'Obbligatoria',
      emailOff: 'Non richiesta',
    },
    credentials: {
      title: 'Credenziali',
      text: 'Le chiavi si salvano fra le credenziali del progetto e non si rileggono. L’Institore non le vede e non te le chiede in chat.',
      noticeTitle: 'Le credenziali non arrivano ancora al sito',
      notice:
        "La piattaforma le salva, ma non le porta ancora nello spazio del sito. L'Institore prepara il codice e il piano; il passaggio finale si fa quando la piattaforma le consegna.",
      saveFailed: 'Salvataggio delle credenziali non riuscito. Riprova.',
      adminOnly: 'Per salvare le credenziali devi amministrare il progetto.',
    },
    note: "Note per l'Institore",
    noteHelp: 'Facoltative. Scrivi che cosa ti aspetti e i vincoli da rispettare.',
    noteRequired: "Scrivi che cosa cambiare nell'accesso.",
    set: 'Set che riceve la richiesta',
    errors: {
      read_only_project: 'Il set ha il progetto in sola lettura: scegli un set che lo abbia in scrittura.',
      profile_conflict: "C'è già una richiesta aperta su questo argomento. Aspetta l'esito o annullala.",
      profile_invalid: 'Dati non validi: {{detail}}',
      queue_full: "La coda dei messaggi del set è piena. Riprova quando l'Institore ha finito.",
      token_limit_reached:
        'Hai usato i token del mese inclusi nel piano. Salva la tua credenziale Claude nel set, oppure attendi il primo del mese.',
      forbidden: 'Per questa modifica devi amministrare il progetto.',
      failed: 'Invio non riuscito. Riprova.',
    },
  },
};

/** Forma dei testi della scheda, usata per tipare le altre lingue. */
export type ProjectProfileTexts = typeof projectProfile;
